import React, { useCallback, useEffect, useState } from 'react';
import { Logger } from '@saviynt/common';
import { Box, Icon, Loader, Typography } from '@saviynt/design-system';
import { useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';

import type { RoleAction } from '../../services';

import { useRoleRecommendationDetails } from '../../hooks/use-rolerecommendations';
import RolesService, {
  type ActivateRoleResponse,
} from '../../services/RolesService/RolesService';
import { normalizeImpactLevel } from '../../shared/utils';
import { getUserKey, getUserName } from '../../utils';
import {
  getErrorMessage,
  isRoleAlreadyExistsError,
} from '../../utils/errorHandlingHttp';
import { useInvertedLocalize } from '../../utils/localization';
import roleDescriptionMessages from '../RolesDashboard/RoleCard/RoleCardDescription/messages';
import generateRoleDescription from '../RolesDashboard/RoleCard/utils/generateRoleDescription';

import AppAccessTiles from './AppAccessTiles/AppAccessTiles';
import Footer from './Footer/Footer';
import RoleDetailsHeader from './RoleDetailsHeader/RoleDetailsHeader';
// MVP2: import RoleInfo from './RoleInfo/RoleInfo';
import {
  type ActivateRoleFormData,
  ActivateRoleModal,
} from './ActivateRoleModal';
import { IncludedEntitlements } from './IncludedEntitlements';
import roleDetailsMessages from './messages';

import './RoleDetails.css';

function RoleDetails() {
  // Get the role ID from the URL params
  const { id } = useParams<{ id: string }>();

  const history = useHistory();

  const intl = useIntl();
  const l = useInvertedLocalize(roleDescriptionMessages);

  const lRoleDetails = useInvertedLocalize(roleDetailsMessages);

  useEffect(() => {
    // auto scroll to top: router uses scroll position from dashboard
    window.scrollTo(0, 0);
  }, []);

  // Modal state
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);

  // Track current role actions (excludes deleted entitlement groups)
  // This is the single source of truth for current role state used by:
  // - AppAccessTiles: Shows apps with correct entitlement counts
  // - ActivateRoleModal: Sends correct actions to activate API
  // - Future components: Any component needing current role data
  const [roleActions, setRoleActions] = useState<RoleAction[]>([]);

  // Handle role data changes from IncludedEntitlements component
  // Called when user deletes entitlement groups or other role modifications occur
  const handleRoleDataChange = useCallback((actions: RoleAction[]) => {
    setRoleActions(actions);
  }, []);

  const roleId = parseInt(id, 10);
  const { data, isLoading, error } = useRoleRecommendationDetails(roleId);

  // check if activate button is disabled based on user access
  const isActivateButtonDisabled = data?.userAccess !== 'edit';

  if (error) {
    return (
      <div>
        {lRoleDetails('Error')}
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {`: ${error instanceof Error ? error.message : String(error)}`}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className='Roles-RoleDetails-loader'>
        <Loader kind='loop' format='main' color='brand' />
      </div>
    );
  }

  if (!data) {
    return <div>{lRoleDetails('Data could not be loaded')}</div>;
  }

  // Check for an empty actions array or any null evKey and redirect to prevent crashes
  if (data.result.data[0]) {
    const roleData = data.result.data[0];

    if (
      roleData.actions.length === 0 ||
      roleData.actions.some(
        (action: RoleAction) => !action.entitlementMetaData.evKey
      )
    ) {
      history.push('../');

      return <div>{lRoleDetails('Redirecting')}</div>;
    }
  }

  const getAppsFromActions = (
    // TODO: remove when we get the count from the API when ready.
    actions: RoleAction[] = []
  ) => {
    const appMap = new Map<
      string,
      { name: string; numEntitlements: number; applicationIcon: string }
    >();

    actions.forEach((action: RoleAction) => {
      const { endpointName, logo } = action.entitlementMetaData.endpoint;
      const existing = appMap.get(endpointName);

      if (existing) {
        existing.numEntitlements += 1;
      } else {
        appMap.set(endpointName, {
          name: endpointName,
          numEntitlements: 1,
          applicationIcon: logo,
        });
      }
    });

    return Array.from(appMap.values());
  };

  // generate the role description for display (with bold formatting JSX)
  const roleDescriptionDisplay =
    data.result.data[0]?.conditions?.length > 0
      ? generateRoleDescription(
          data.result.data[0].conditions,
          l as (defaultMessage: string, placeholders?: object) => string,
          intl,
          { boldAttributesAndValues: true }
        )
      : '';

  // generate the role description for modal description field (plain icu string)
  const roleDescriptionPlain =
    data.result.data[0]?.conditions?.length > 0
      ? (generateRoleDescription(
          data.result.data[0].conditions,
          l as (defaultMessage: string, placeholders?: object) => string,
          intl
        ) as string)
      : '';

  // Handle opening the activate modal
  const handleActivateRole = () => {
    setIsActivateModalOpen(true);
  };

  // Handle closing the activate modal
  const handleCloseModal = () => {
    setIsActivateModalOpen(false);
    setIsSaving(false);
    setIsActivating(false);
    setActivationError(null);
  };

  const handleClearActivationError = () => {
    setActivationError(null);
  };

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const handleActivateSuccess = (
    response: ActivateRoleResponse,
    formData: ActivateRoleFormData
  ) => {
    try {
      // Store success data for banner display on dashboard
      sessionStorage.setItem(
        'roleActivationSuccess',
        JSON.stringify({
          roleName: formData.roleName,
          roleId: response.roleId,
          timestamp: Date.now(), // For cleanup safety
        })
      );
    } catch (storageError) {
      Logger.warn(
        'SessionStorage failed, redirecting without banner',
        storageError
      );
    }

    window.location.href = '../';
  };

  const handleActivateError = (
    apiError: Error,
    formData: ActivateRoleFormData
  ) => {
    // Check if it's a duplicate role name error
    if (isRoleAlreadyExistsError(apiError)) {
      setActivationError(
        lRoleDetails('A role already exists in the system with this name')
      );
      setIsActivating(false);

      return;
    }

    // For all other errors, redirect to dashboard with error banner
    try {
      // Store error data for banner display on dashboard
      sessionStorage.setItem(
        'roleActivationError',
        JSON.stringify({
          roleName: formData.roleName,
          errorMessage: getErrorMessage(apiError),
          timestamp: Date.now(),
        })
      );
    } catch (storageError) {
      Logger.warn(
        'SessionStorage failed, redirecting without banner',
        storageError
      );
    }

    window.location.href = '../';
  };

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const handleSave = (formData: ActivateRoleFormData) => {
    // TODO: save btn is for future mvp - implement api call
    setIsSaving(true);

    // await saveRole(roleId, formData); // future mvp

    // Simulate API call
    setTimeout(() => {
      Logger.info('Role saved successfully');
      handleCloseModal();
    }, 1000);
  };

  // Handle save and activate action
  const handleSaveAndActivate = async (
    formData: ActivateRoleFormData,
    actions: RoleAction[]
  ) => {
    setIsActivating(true);

    try {
      const userKey = getUserKey();
      const userName = getUserName();

      if (!userKey) {
        throw new Error('User key not found');
      }

      if (!userName) {
        throw new Error('Username not found');
      }

      // Transform actions to the format expected by the API
      const apiActions = actions.map((action) => ({
        entitlementValueName: action.entitlementMetaData.entitlementValueName,
        entitlementTypeName:
          action.entitlementMetaData.entitlementType.entitlementTypeName,
        endpointName: action.entitlementMetaData.endpoint.endpointName,
      }));

      const requestBody = {
        activatedBy: parseInt(userKey, 10),
        name: formData.roleName,
        description: formData.description,
        owner: userName, // TODO: confirm this is the logged in username
        entitlements: apiActions,
        requestable: formData.isRequestable,
      };

      const response = await RolesService.activateRole(
        roleId.toString(),
        requestBody
      );

      handleActivateSuccess(response, formData);
    } catch (apiError) {
      Logger.error('Failed to activate role:', apiError);

      handleActivateError(apiError as Error, formData);
    }
  };

  return (
    <Box className='Roles-RoleDetails'>
      <RoleDetailsHeader
        title={lRoleDetails(
          '{count, plural, one {# Included User} other {# Included Users}}',
          { count: data.result.data[0]?.includedUsers ?? 0 }
        )}
        subtitle={lRoleDetails(
          'Review the description and included access before converting to a Role.'
        )}
        showBreadcrumbs
        impactLevel={normalizeImpactLevel(data.result.data[0]?.impact)}
        icon={<Icon kind='Security' size='large' />}
      />

      {/* role description section - localized ICU string with bold formatting */}
      <Box tag='section' className='Roles-RoleDescription'>
        <Box tag='header' className='Roles-RoleDescription-header'>
          <Typography kind='h3' className='Roles-RoleDescription-title'>
            {lRoleDetails('Role Description')}
          </Typography>
        </Box>
        <Box className='Roles-RoleDescription-content'>
          <Box className='Roles-RoleDescription-box'>
            <Typography
              kind='body2'
              color='--color-foreground-secondary-bold'
              className='Roles-RoleDescription-text'>
              {roleDescriptionDisplay}
            </Typography>
          </Box>
        </Box>
      </Box>

      <AppAccessTiles
        // Uses current role actions (excludes deleted entitlement groups)
        // This ensures app tiles show only apps that still exist in the role
        title={lRoleDetails('Access will be provided to these apps')}
        apps={getAppsFromActions(
          roleActions.length > 0 ? roleActions : data.result.data[0]?.actions
        )}
      />

      <IncludedEntitlements
        actions={data.result.data[0]?.actions ?? []}
        roleData={
          data.result.data[0]
            ? {
                userMatchCount: data.result.data[0].userMatchCount,
                confidence: data.result.data[0].confidence,
                entitlements: data.result.data[0].entitlementsCount, // backend changed from entitlements
                conditions: data.result.data[0].conditions,
              }
            : undefined
        }
        onRoleDataChange={handleRoleDataChange} // Callback to receive current role state
      />

      <Footer
        onBack={() => {
          history.replace('../');
        }}
        onActivateRole={handleActivateRole}
        isActivating={isActivating}
        isActivateButtonDisabled={isActivateButtonDisabled}
      />

      {/* Activate Role Modal */}
      <ActivateRoleModal
        isOpen={isActivateModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        onSaveAndActivate={(formData, actions) => {
          handleSaveAndActivate(formData, actions).catch((err) => {
            Logger.error('Unhandled activation error', err);
          });
        }}
        isSaving={isSaving}
        isActivating={isActivating}
        actions={roleActions} // Uses current role actions (excludes deleted entitlement groups)
        errorMessage={activationError ?? undefined}
        onClearError={handleClearActivationError}
        defaultDescription={roleDescriptionPlain}
      />
    </Box>
  );
}

export default RoleDetails;
